// Redux Toolkit slice for Storage Management with Lakehouse (RTK Query under the hood)
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../index';
import apiSlice from './apiSlice';

// Initial state
const initialState = {
    files: [],
    total: 0,
    page: 1,
    pageSize: 50,
    totalPages: 0,

    // Analytics
    analytics: null,

    // Migration
    migrationStatus: null,
    isMigrating: false,

    // Lakehouse stats
    lakehouseStats: null,

    // UI state
    loading: false,
    error: null,
    selectedFiles: [],

    // Filters
    filters: {
        search: '',
        storageBackend: null,
        lifecycleTier: null,
        migrationStatus: null,
        projectId: null,  // Filter by project ID
        folderPath: null  // Filter by folder path (null = all, "" = root, "folder" = specific folder)
    }
};

// Create slice
const slice = createSlice({
    name: 'storage',
    initialState,
    reducers: {
        // Set files
        setFiles(state, action) {
            state.files = action.payload.files || [];
            state.total = action.payload.total || 0;
            state.page = action.payload.page || 1;
            state.pageSize = action.payload.page_size || 50;
            state.totalPages = action.payload.total_pages || 0;
        },

        // Set analytics
        setAnalytics(state, action) {
            state.analytics = action.payload;
        },

        // Set migration status
        setMigrationStatus(state, action) {
            state.migrationStatus = action.payload;
            state.isMigrating = action.payload?.is_migration_running || false;
        },

        // Set lakehouse stats
        setLakehouseStats(state, action) {
            state.lakehouseStats = action.payload;
        },

        // Update file in list
        updateFile(state, action) {
            const index = state.files.findIndex((f) => f.id === action.payload.id);
            if (index !== -1) {
                state.files[index] = action.payload;
            }
        },

        // Remove file from list
        removeFile(state, action) {
            state.files = state.files.filter((f) => f.id !== action.payload);
            state.total -= 1;
        },

        // Set loading
        setLoading(state, action) {
            state.loading = action.payload;
        },

        // Set error
        setError(state, action) {
            state.error = action.payload;
        },

        // Set selected files
        setSelectedFiles(state, action) {
            state.selectedFiles = action.payload;
        },

        // Update filters
        updateFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },

        // Clear filters
        clearFilters(state) {
            state.filters = initialState.filters;
        },

        // Pagination (for RTK Query sync / manual set)
        setPage(state, action) {
            state.page = action.payload;
        },
        setPageSize(state, action) {
            state.pageSize = action.payload;
        }
    }
});

// Export reducer
export default slice.reducer;

// Export actions
export const {
    setFiles,
    setPage,
    setPageSize,
    setAnalytics,
    setMigrationStatus,
    setLakehouseStats,
    updateFile,
    removeFile,
    setLoading,
    setError,
    setSelectedFiles,
    updateFilters,
    clearFilters
} = slice.actions;

// Simple helper for auth/error handling
const handleAuthError = (error, dispatch) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 401) {
        dispatch(setError('Unauthorized (401)'));
        return true; // không redirect để dễ debug
    }
    if (status === 403) {
        dispatch(setError('Not authorized to access this resource'));
        return true;
    }
    return false;
};

// ----------------------------------------------------------------------
// Async actions (thunks)
// ----------------------------------------------------------------------

// Fetch files with lakehouse info
export function fetchStorageFiles(page = 1, pageSize = 50) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const { filters } = getState().storage;

            const params = {
                page,
                page_size: pageSize,
                ...(filters.search && { search: filters.search }),
                ...(filters.storageBackend && { storage_backend: filters.storageBackend }),
                ...(filters.lifecycleTier && { lifecycle_tier: filters.lifecycleTier }),
                ...(filters.migrationStatus && { migration_status: filters.migrationStatus }),
                ...(filters.projectId && { project_id: filters.projectId }),
                // Only send folder_path if it's a specific folder (not null/undefined)
                // null/undefined means show all files for grouping folders
                ...(filters.folderPath && { folder_path: filters.folderPath })
            };

            const result = await dispatch(apiSlice.endpoints.getStorageFiles.initiate(params)).unwrap();
            dispatch(setFiles(result));
        } catch (error) {
            console.error('Error fetching storage files:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || error?.response?.data?.detail || 'Failed to fetch files'));
            }
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Upload file to lakehouse
export function uploadFileToLakehouse(file, projectId = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const formData = new FormData();
            formData.append('file', file);
            if (projectId) {
                formData.append('project_id', projectId);
            }

            const data = await dispatch(
                apiSlice.endpoints.uploadStorageFile.initiate({ file, project_id: projectId })
            ).unwrap();
            dispatch(fetchStorageFiles());
            return data;
        } catch (error) {
            console.error('Error uploading file:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Upload failed'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Delete file
export function deleteStorageFile(fileId) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            await dispatch(apiSlice.endpoints.deleteStorageFile.initiate(fileId)).unwrap();
            dispatch(removeFile(fileId));
        } catch (error) {
            console.error('Error deleting file:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Delete failed'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Bulk delete files
export function bulkDeleteFiles(fileIds) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const response = await dispatch(
                apiSlice.endpoints.bulkDeleteStorageFiles.initiate({ file_ids: fileIds })
            ).unwrap();
            const results = response?.results || {};
            Object.keys(results).forEach((id) => {
                if (results[id].success) dispatch(removeFile(id));
            });
            return response;
        } catch (error) {
            console.error('Error bulk deleting files:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Bulk delete failed'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Update file lifecycle
export function updateFileLifecycle(fileId, lifecycleTier) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.updateStorageFileLifecycle.initiate({ fileId, lifecycle_tier: lifecycleTier })
            ).unwrap();
            dispatch(updateFile(result));
        } catch (error) {
            console.error('Error updating lifecycle:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Update failed'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Fetch analytics
export function fetchStorageAnalytics() {
    return async (dispatch) => {
        try {
            const result = await dispatch(apiSlice.endpoints.getStorageAnalytics.initiate(undefined)).unwrap();
            dispatch(setAnalytics(result));
        } catch (error) {
            console.error('Error fetching analytics:', error);
            handleAuthError(error, dispatch);
        }
    };
}

// Fetch migration status
export function fetchMigrationStatus() {
    return async (dispatch) => {
        try {
            const result = await dispatch(apiSlice.endpoints.getStorageMigrationStatus.initiate(undefined)).unwrap();
            dispatch(setMigrationStatus(result));
        } catch (error) {
            console.error('Error fetching migration status:', error);
            handleAuthError(error, dispatch);
        }
    };
}

// Start migration
export function startMigration(batchSize = 50, storageBackendFilter = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.startStorageMigration.initiate({
                    batch_size: batchSize,
                    storage_backend_filter: storageBackendFilter
                })
            ).unwrap();
            setTimeout(() => dispatch(fetchMigrationStatus()), 2000);
            return result;
        } catch (error) {
            console.error('Error starting migration:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Migration start failed'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Fetch lakehouse stats
export function fetchLakehouseStats() {
    return async (dispatch) => {
        try {
            const result = await dispatch(apiSlice.endpoints.getLakehouseStats.initiate(undefined)).unwrap();
            dispatch(setLakehouseStats(result));
        } catch (error) {
            console.error('Error fetching lakehouse stats:', error);
            handleAuthError(error, dispatch);
        }
    };
}

// Preview file
export function previewFile(fileId) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.getStorageFilePreview.initiate(fileId)
            ).unwrap();
            return result;
        } catch (error) {
            console.error('Error getting preview:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to get preview'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// =====================================================
// File Sharing Actions
// =====================================================

// Create file share
export function createFileShare(fileId, expiresIn = 3600, password = null, maxDownloads = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.createStorageFileShare.initiate({
                    fileId,
                    expires_in: expiresIn,
                    password,
                    max_downloads: maxDownloads
                })
            ).unwrap();
            return result;
        } catch (error) {
            console.error('Error creating file share:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to create share'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// List file shares
export function listFileShares(fileId) {
    return async (dispatch) => {
        try {
            const result = await dispatch(
                apiSlice.endpoints.getStorageFileShares.initiate(fileId)
            ).unwrap();
            return result ?? [];
        } catch (error) {
            console.error('Error listing file shares:', error);
            handleAuthError(error, dispatch);
            throw error;
        }
    };
}

// Revoke file share
export function revokeFileShare(fileId, shareId) {
    return async (dispatch) => {
        try {
            await dispatch(
                apiSlice.endpoints.deleteStorageFileShare.initiate({ fileId, shareId })
            ).unwrap();
            return { success: true };
        } catch (error) {
            console.error('Error revoking file share:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to revoke share'));
            }
            throw error;
        }
    };
}

// =====================================================
// Advanced Search Actions
// =====================================================

// Advanced search files
export function searchFiles(searchRequest) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.searchStorageFiles.initiate(searchRequest)
            ).unwrap();
            dispatch(setFiles(result));
            return result;
        } catch (error) {
            console.error('Error searching files:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Search failed'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// =====================================================
// Folder Management Actions
// =====================================================

// Create folder
export function createFolder(folderName, parentPath = null, projectId = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const params = projectId ? { project_id: projectId } : {};
            const result = await dispatch(
                apiSlice.endpoints.createStorageFolder.initiate({
                    folder_name: folderName,
                    parent_path: parentPath,
                    project_id: projectId ?? undefined
                })
            ).unwrap();
            return result;
        } catch (error) {
            console.error('Error creating folder:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to create folder'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// List folders
export function listFolders(projectId = null, parentPath = null) {
    return async (dispatch) => {
        try {
            const result = await dispatch(
                apiSlice.endpoints.getStorageFolders.initiate({
                    project_id: projectId ?? undefined,
                    parent_path: parentPath ?? undefined
                })
            ).unwrap();
            return result ?? [];
        } catch (error) {
            console.error('Error listing folders:', error);
            handleAuthError(error, dispatch);
            throw error;
        }
    };
}

// Move files
export function moveFiles(fileIds, destinationPath) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.moveStorageFiles.initiate({
                    file_ids: fileIds,
                    destination_path: destinationPath
                })
            ).unwrap();
            dispatch(fetchStorageFiles());
            return result;
        } catch (error) {
            console.error('Error moving files:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to move files'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Copy files
export function copyFiles(fileIds, destinationPath) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const result = await dispatch(
                apiSlice.endpoints.copyStorageFiles.initiate({
                    file_ids: fileIds,
                    destination_path: destinationPath
                })
            ).unwrap();
            dispatch(fetchStorageFiles());
            return result;
        } catch (error) {
            console.error('Error copying files:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to copy files'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Delete folder
export function deleteFolder(folderPath, projectId = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const params = projectId ? { project_id: projectId } : {};
            await dispatch(
                apiSlice.endpoints.deleteStorageFolder.initiate({
                    folderPath,
                    project_id: projectId ?? undefined
                })
            ).unwrap();
            dispatch(fetchStorageFiles());
            return { success: true };
        } catch (error) {
            console.error('Error deleting folder:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to delete folder'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// =====================================================
// Storage Quota Actions
// =====================================================

// Get quota
export function getStorageQuota() {
    return async (dispatch) => {
        try {
            const result = await dispatch(apiSlice.endpoints.getStorageQuota.initiate(undefined)).unwrap();
            return result;
        } catch (error) {
            console.error('Error fetching quota:', error);
            handleAuthError(error, dispatch);
            throw error;
        }
    };
}

// Update quota (admin only)
export function updateStorageQuota(quotaBytes = null, maxFileCount = null, userId = null, tenantId = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        dispatch(setError(null));

        try {
            const params = {};
            if (userId) params.user_id = userId;
            if (tenantId) params.tenant_id = tenantId;
            
            const result = await dispatch(
                apiSlice.endpoints.updateStorageQuota.initiate({
                    quota_bytes: quotaBytes,
                    max_file_count: maxFileCount,
                    user_id: userId,
                    tenant_id: tenantId
                })
            ).unwrap();
            return result;
        } catch (error) {
            console.error('Error updating quota:', error);
            if (!handleAuthError(error, dispatch)) {
                dispatch(setError(error?.data?.detail || 'Failed to update quota'));
            }
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}


// Apply filters and refresh
export function applyFilters(filters) {
    return async () => {
        dispatch(updateFilters(filters));
        dispatch(fetchStorageFiles());
    };
}

// =====================================================
// Chatbot Actions - RAG Integration
// =====================================================

// Fetch all chatbots for current user (RTK)
export function fetchChatbots() {
    return async (dispatch) => {
        try {
            const data = await dispatch(apiSlice.endpoints.getChatbots.initiate(undefined)).unwrap();
            return { payload: Array.isArray(data) ? data : (data ?? []) };
        } catch (error) {
            console.error('Error fetching chatbots:', error);
            if (!handleAuthError(error, dispatch)) return { payload: [] };
            return { payload: [] };
        }
    };
}

export function createChatbot(name, description, llmModel) {
    return async (dispatch) => {
        try {
            const data = await dispatch(
                apiSlice.endpoints.createChatbot.initiate({ name, description, llm_model: llmModel })
            ).unwrap();
            return { payload: data };
        } catch (error) {
            console.error('Error creating chatbot:', error);
            if (!handleAuthError(error, dispatch)) throw error;
        }
    };
}

export function addDocumentsToChatbot(chatbotId, fileIds) {
    return async (dispatch) => {
        try {
            const data = await dispatch(
                apiSlice.endpoints.addDocumentsToChatbot.initiate({ chatbotId, fileIds })
            ).unwrap();
            return { payload: data };
        } catch (error) {
            console.error('Error adding documents to chatbot:', error);
            if (!handleAuthError(error, dispatch)) throw error;
        }
    };
}

export function createChatbotWithDocuments(name, description, llmModel, fileIds) {
    return async (dispatch) => {
        try {
            const chatbot = await dispatch(
                apiSlice.endpoints.createChatbot.initiate({ name, description, llm_model: llmModel })
            ).unwrap();
            const chatbotId = chatbot?.id;
            if (chatbotId && fileIds?.length > 0) {
                await dispatch(
                    apiSlice.endpoints.addDocumentsToChatbot.initiate({ chatbotId, fileIds })
                ).unwrap();
            }
            return { payload: chatbot };
        } catch (error) {
            console.error('Error creating chatbot with documents:', error);
            if (!handleAuthError(error, dispatch)) throw error;
        }
    };
}
